'use client';

import Link, { type LinkProps } from 'next/link';
import { type ComponentPropsWithoutRef, forwardRef } from 'react';
import { useLocale } from '@/lib/locale-context';
import { isNonLocalizedPath } from '@/lib/i18n';

type LocaleLinkProps = Omit<LinkProps, 'href'> &
  Omit<ComponentPropsWithoutRef<'a'>, keyof LinkProps | 'href'> & {
    href: string;
    external?: boolean;
  };

/**
 * Locale-aware Link for use under app/[locale]/.
 *
 * Behavior:
 *   - If locale === 'en': href is unchanged (English at root, no prefix)
 *   - If locale !== 'en': href is prefixed with /<locale>
 *   - External URLs (http://, https://, mailto:, tel:) are never modified
 *   - Anchor-only links (#...) are never modified
 *   - Hashes inside paths are preserved
 */
export const LocaleLink = forwardRef<HTMLAnchorElement, LocaleLinkProps>(
  function LocaleLink({ href, external, ...props }, ref) {
    const locale = useLocale();
    const localized = localizeHref(href, locale);

    if (external) {
      return <a {...(props as ComponentPropsWithoutRef<'a'>)} href={localized} ref={ref} />;
    }
    return <Link {...(props as LinkProps)} href={localized} ref={ref} />;
  },
);

function localizeHref(href: string, locale: string): string {
  if (!href) return href;
  if (
    href.startsWith('http://') ||
    href.startsWith('https://') ||
    href.startsWith('mailto:') ||
    href.startsWith('tel:') ||
    href.startsWith('#')
  ) {
    return href;
  }
  if (locale === 'en') return href;
  if (isNonLocalizedPath(href)) return href;
  if (href.startsWith(`/${locale}/`) || href === `/${locale}`) return href;
  if (href.startsWith('/')) return `/${locale}${href}`;
  return href;
}

/**
 * Pure server-safe helper for Server Components that need to build a
 * locale-aware path. Same logic as localizeHref, but exported for use
 * outside the React tree (e.g. inside generateMetadata).
 */
export function buildLocalePath(locale: string, path: string): string {
  return localizeHref(path, locale);
}
